import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Language } from '../../types_i18n';
import './LanguageSelection.css';

const OPTIONS: { code: Language; flag: string; label: string }[] = [
  { code: 'nl', flag: '🇳🇱', label: 'Nederlands' },
  { code: 'en', flag: '🇬🇧', label: 'English' },
  { code: 'ar', flag: '🇸🇦', label: 'العربية' },
];

export default function LanguageSelection() {
  const navigate = useNavigate();
  const { setLanguage } = useLanguage();
  const [selectedLang, setSelectedLang] = useState<Language | null>(null);

  // This screen is Dutch-only; keep layout LTR regardless of previously chosen RTL language.
  React.useEffect(() => {
    const root = document.documentElement;
    const prevDir = root.getAttribute('dir');
    const prevLang = root.getAttribute('lang');
    root.setAttribute('dir', 'ltr');
    root.setAttribute('lang', 'nl');
    return () => {
      if (prevDir) root.setAttribute('dir', prevDir);
      else root.removeAttribute('dir');
      if (prevLang) root.setAttribute('lang', prevLang);
      else root.removeAttribute('lang');
    };
  }, []);

  const select = async (lang: Language) => {
    setSelectedLang(lang);
    setLanguage(lang);
    try {
      localStorage.setItem('preferredLanguage', lang);
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
      if (lang === 'ar') {
        document.documentElement.setAttribute('dir', 'rtl');
        document.documentElement.setAttribute('lang', 'ar');
      } else {
        document.documentElement.setAttribute('dir', 'ltr');
        document.documentElement.setAttribute('lang', lang);
      }
    } catch {
      // ignore
    }
    // Delay navigation to show selection feedback
    setTimeout(() => navigate('/dashboard'), 240);
  };

  return (
    <div className="language-screen tc-page tc-page--fullscreen">
      <button className="onboarding-back-btn" onClick={() => navigate('/dashboard')}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="20" height="20">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>

      <div className="onboarding-step-dots">
        <div className="onboarding-step-dot active" />
        <div className="onboarding-step-dot" />
        <div className="onboarding-step-dot" />
      </div>

      <div className="onboarding-content tc-container tc-container--tight">
        <h1 className="onboarding-title">In welke taal wil je oefenen?</h1>
        <p className="onboarding-subtitle">
          We starten met een korte test om te zien of je al klaar bent voor het examen.
        </p>
        <div className="language-diagnostic-badge">
          <span className="language-diagnostic-badge__dot" />
          <span>Diagnosetoets · 25 vragen · ~10 minuten</span>
        </div>

        <div className="language-options">
          {OPTIONS.map(({ code, flag, label }) => (
            <button
              key={code}
              type="button"
              onClick={() => select(code)}
              className={`language-option${selectedLang === code ? ' selected' : ''}`}
            >
              <span className="language-flag">{flag}</span>
              <span className="language-info">
                <span className="language-name">{label}</span>
              </span>
              <span className="language-checkmark">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" width="12" height="12">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </span>
            </button>
          ))}
        </div>

        <p className="language-hint-text">Tik op je taal om door te gaan</p>
      </div>
    </div>
  );
}

